import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { z } from "zod/v4";
import { ProvenanceTemplate, VideoTutorialBox } from "./-components";
import type { CSSProperties } from "react";
import type {
  AiDetectionHighlighterSentence,
  AiHighlighterResult,
  AiHighlighterResultV2,
  ProvenanceV2EntryBase,
} from "@/lib/types/provenance";
import { aiHighlighter } from "@/lib/textAnalyze";
import DetectedPercentages, {
  DetectedPercentagesV2,
} from "@/components/text-analyze/DetectedPercentages";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

function isFormatV2(result: unknown): result is AiHighlighterResultV2 {
  return typeof result === "object" && result !== null && "text" in result;
}

const aiDetectorSearchSchema = z.object({
  appSumoGreeting: z.boolean().optional().default(false),
});
export const Route = createFileRoute("/_app/_provenance/ai-detector")({
  component: RouteComponent,
  validateSearch: aiDetectorSearchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        appSumoGreeting: false,
      }),
    ],
  },
  staticData: {
    title: "AI Detector",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Spot AI-generated or altered text instantly with Polygraf AI’s AI Detector - 5×-more-accurate pattern analysis, enterprise-grade governance controls, and local or on-premises deployments.",
      },
    ],
  }),
});

function RouteComponent() {
  const { title } = Route.useMatch({
    select: (m) => m.staticData,
  });

  return (
    <div className="relative">
      <VideoTutorialBox
        title="New to Polygraf?"
        description="Learn how to use AI Detector"
        youtubeUrl="https://youtu.be/_If6cJk7X68"
      />
      <AppSumoPopup />
      <ProvenanceTemplate
        title={title!}
        subtitle="Determine if content is human-written, AI-generated, or altered."
        model="AI_DETECTION_HIGHLIGHTER"
        analyze={(text) => aiHighlighter(text)}
        SuccessSection={AiDetectorResult}
        SuccessTextAreaReplacement={HighligtedText}
        nextPage={{
          title: "Humanized Text Detector",
          linkOptions: {
            to: "/humanized-text-detector",
          },
        }}
        __addHumanizeButton={(data) => {
          const result = data.result as
            | AiHighlighterResult
            | AiHighlighterResultV2;
          if (isFormatV2(result)) {
            return result.overall_score.AI > 0.1;
          }
          const ai_percentage = Object.entries(result.overall_score)
            .filter(([k]) =>
              [
                "CLAUDE",
                "GEMINI",
                "MISTRAL",
                "OPEN_AI",
                "GRAMMAR_ENHANCED",
              ].includes(k),
            )
            .reduce((acc, [_, v]) => v + acc, 0);
          return ai_percentage > 0.1;
        }}
      />
    </div>
  );
}

type HighlightLabel =
  | "AI"
  // | "ALTERED"
  | "HUMAN";
const highlightColors = {
  AI: "#FB4141",
  ALTERED: "#F9A544",
  HUMAN: "#00E396",
} as const satisfies Record<HighlightLabel | "ALTERED", string>;

function AiDetectorResult({
  entry,
}: {
  entry: ProvenanceV2EntryBase<"AI_DETECTION_HIGHLIGHTER", string>;
}) {
  const result = entry.result as AiHighlighterResult | AiHighlighterResultV2;

  return (
    <>
      <div className="mt-4 mb-4 h-[2px] bg-gray-200 dark:bg-gray-700"></div>
      <div className="mb-4 flex flex-wrap gap-3">
        {["Altered", "AI", "Human"].map((label) => (
          <div
            key={label}
            className="flex max-w-full items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-transparent"
          >
            <div
              style={
                {
                  "--hl-color":
                    highlightColors[label.toUpperCase() as HighlightLabel],
                } as CSSProperties
              }
              className="size-4 rounded-full bg-(--hl-color)"
            ></div>
            <span className="text-gray-700 dark:text-white">{label}</span>
          </div>
        ))}
      </div>

      {isFormatV2(result) ? (
        <DetectedPercentagesV2 results={result.overall_score} />
      ) : (
        <DetectedPercentages results={result.overall_score} />
      )}

      {/* backend doesn't return this yet in the v2 endpoint */}
      {"cognitive_flow_index" in result && ( // only present in old format
        <div className="rounded-lg border-2 border-gray-200 bg-[#FAFAFA] p-4 dark:border-gray-800 dark:bg-transparent">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="size-16 items-center justify-center rounded-[10px] border-2 border-gray-200 bg-white p-2">
                <img
                  src="/icons/bulb-icon.svg"
                  alt="Bulb Icon"
                  className="size-12 dark:text-black"
                />
              </div>
            </div>
            <span className="text-3xl font-bold">
              {(result.cognitive_flow_index as number).toFixed(2)}
            </span>
          </div>

          <h3 className="mb-2 text-lg font-semibold">Cognitive Flow Index</h3>
          <p className="text-sm">
            Measures how natural, creative, and human-like your text feels.
            Higher scores indicate dynamic and engaging writing, while lower
            scores suggest repetitive or mechanical patterns
          </p>
        </div>
      )}
    </>
  );
}

export default function AppSumoPopup() {
  const appSumoGreeting = Route.useSearch({
    select: (s) => s.appSumoGreeting,
  });
  const navigate = Route.useNavigate();
  const [open, _setOpen] = useState(appSumoGreeting);
  const setOpen = useCallback<typeof _setOpen>(
    (v) =>
      _setOpen((old) => {
        const newV = typeof v === "function" ? v(old) : v;
        if (!newV)
          navigate({
            search: {
              appSumoGreeting: false,
            },
          });
        return newV;
      }),
    [_setOpen, navigate],
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-3xl bg-white p-8 sm:max-w-md">
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
                <div className="rounded-ful flex h-8 w-8 items-center justify-center">
                  <img
                    src="/icons/check.svg"
                    alt="Check Icon"
                    className="h-8 w-8 text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <h2 className="text-2xl leading-tight font-semibold text-gray-900">
            Your AppSumo subscription has been activated!
          </h2>

          <Button
            onClick={() => setOpen(false)}
            className="w-full rounded-[10px] bg-gray-700 py-4 text-lg font-medium text-white hover:bg-gray-600"
            size="lg"
          >
            Okay
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HighligtedText({
  entry,
}: {
  entry: ProvenanceV2EntryBase<"AI_DETECTION_HIGHLIGHTER", string>;
}) {
  const result = entry.result as AiHighlighterResult | AiHighlighterResultV2;

  if (isFormatV2(result)) {
    const { text, sentence_data } = result;
    const sorted = [...sentence_data].sort((a, b) => a.start - b.start);

    const segments: Array<{ text: string; label?: string }> = [];
    let cursor = 0;

    for (const sentence of sorted) {
      if (sentence.start > cursor) {
        segments.push({ text: text.slice(cursor, sentence.start) });
      }
      segments.push({
        text: text.slice(sentence.start, sentence.end),
        label: sentence.label,
      });
      cursor = sentence.end;
    }

    if (cursor < text.length) {
      segments.push({ text: text.slice(cursor) });
    }

    return (
      <div className="min-h-[400px] overflow-auto p-6">
        <p className="leading-7">
          {segments.map((seg, i) =>
            seg.label ? (
              <span
                key={i}
                style={
                  {
                    "--hl-color":
                      (highlightColors[seg.label as HighlightLabel] as
                        | string
                        | undefined) ?? "#F9A544",
                  } as CSSProperties
                }
                className="bg-(--hl-color)/40"
              >
                {seg.text}
              </span>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </p>
      </div>
    );
  }

  // Old format: group sentences by paragraph_idx
  const paragraphs = result.sentence_data.reduce<
    Array<Array<AiDetectionHighlighterSentence>>
  >((acc, val) => {
    const i = val.paragraph_idx;
    if (Array.isArray(acc[i])) acc[i].push(val);
    else acc[i] = [val];
    return acc;
  }, []);

  return (
    <div className="min-h-[400px] overflow-auto p-6">
      {paragraphs.map((paragraph) => (
        <p
          className="leading-7 [&:not(:first-child)]:mt-6"
          key={paragraph.map(({ sentence }) => sentence).join("-")}
        >
          {paragraph.map(({ sentence, label, score }, index) => (
            <span
              key={sentence + label + score}
              style={
                {
                  "--hl-color":
                    (highlightColors[label as HighlightLabel] as
                      | string
                      | undefined) ?? "#F9A544",
                } as CSSProperties
              }
              className={`${index !== 0 ? "ml-2" : ""} bg-(--hl-color)/40`}
            >
              {sentence}
            </span>
          ))}
          <br />
        </p>
      ))}
    </div>
  );
}
